// Fragmented MP4 from NX, as the continuous player needs it (continuous.ts): split a byte stream into top-level boxes,
// read the init segment (codecs for Media Source, the stream's wall-clock start) and put each fragment on one timeline.
//
// What NX sends (6.1, measured on staging 2026-10-27): ftyp, moov (one trak per track, empty sample tables, a udta
// comment whose JSON has "startTimeMs": the wall time the stream's media time 0 stands for), then moof + mdat pairs, one
// GOP each. A traf has tfhd, trun (sample durations and sizes, data offset from the moof) and a Smooth Streaming time box
// (uuid 6d1d9b05-…, "tfxd": the fragment's media time and length), but no tfdt. Media time starts near 0 in every
// stream, so the same frame from two streams has the same startTimeMs + tfxd, to the millisecond. Media Source needs
// tfdt to place a fragment, so each moof gets one, on a timeline whose 0 is the first stream's start.

export interface Box {
  type: string;
  bytes: Uint8Array;
}

/** Splits a byte stream into complete top-level boxes as the chunks arrive. */
export class BoxSplitter {
  private buf = new Uint8Array(0);

  push(chunk: Uint8Array): Box[] {
    const joined = new Uint8Array(this.buf.length + chunk.length);
    joined.set(this.buf);
    joined.set(chunk, this.buf.length);
    const out: Box[] = [];
    let o = 0;
    for (;;) {
      const h = header(joined, o, joined.length);
      if (!h || h.size === 0 || o + h.size > joined.length) break;
      out.push({ type: h.type, bytes: joined.subarray(o, o + h.size) });
      o += h.size;
    }
    this.buf = joined.slice(o);
    return out;
  }
}

interface Header {
  type: string;
  size: number;
  /** Header length (8, or 16 with a 64-bit size). */
  hdr: number;
}

function header(b: Uint8Array, o: number, end: number): Header | null {
  if (o + 8 > end) return null;
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let size = dv.getUint32(o);
  const type = String.fromCharCode(b[o + 4]!, b[o + 5]!, b[o + 6]!, b[o + 7]!);
  let hdr = 8;
  if (size === 1) {
    if (o + 16 > end) return null;
    size = Number(dv.getBigUint64(o + 8));
    hdr = 16;
  }
  if (size !== 0 && size < hdr) return { type, size: 0, hdr };
  return { type, size, hdr };
}

/** The child boxes of a container box's body (bytes from `start`, relative to `b`). */
function children(b: Uint8Array, start: number, end = b.length): (Header & { at: number })[] {
  const out: (Header & { at: number })[] = [];
  let o = start;
  while (o + 8 <= end) {
    const h = header(b, o, end);
    if (!h || h.size === 0 || o + h.size > end) break;
    out.push({ ...h, at: o });
    o += h.size;
  }
  return out;
}

const find = (b: Uint8Array, start: number, end: number, type: string) => children(b, start, end).find((c) => c.type === type);
const view = (b: Uint8Array) => new DataView(b.buffer, b.byteOffset, b.byteLength);
const hex2 = (n: number) => n.toString(16).padStart(2, '0');

export interface Track {
  id: number;
  timescale: number;
  kind: 'video' | 'audio' | 'other';
  /** RFC 6381 codec string for Media Source; null when this player can't name it. */
  codec: string | null;
}

export interface Init {
  /** Wall time (epoch ms) of media time 0; null when NX didn't say. */
  startMs: number | null;
  tracks: Track[];
  /** The Media Source type, or null when a track's codec is unknown. */
  mime: string | null;
}

/** Reads moov: tracks, their timescales and codecs, and NX's startTimeMs. */
export function parseInit(moov: Uint8Array): Init {
  const tracks: Track[] = [];
  let startMs: number | null = null;
  for (const c of children(moov, 8)) {
    if (c.type === 'trak') tracks.push(parseTrak(moov, c.at + c.hdr, c.at + c.size));
    if (c.type === 'udta') {
      const text = new TextDecoder('latin1').decode(moov.subarray(c.at, c.at + c.size));
      const m = /"startTimeMs"\s*:\s*"?(\d{10,})/.exec(text);
      if (m) startMs = Number(m[1]);
    }
  }
  const media = tracks.filter((t) => t.kind !== 'other');
  const mime = media.length && media.every((t) => t.codec) ? `video/mp4; codecs="${media.map((t) => t.codec).join(', ')}"` : null;
  return { startMs, tracks, mime };
}

function parseTrak(b: Uint8Array, start: number, end: number): Track {
  const dv = view(b);
  const tkhd = find(b, start, end, 'tkhd');
  const id = tkhd ? dv.getUint32(tkhd.at + tkhd.hdr + (b[tkhd.at + tkhd.hdr] === 1 ? 20 : 12)) : 0;
  const mdia = find(b, start, end, 'mdia');
  let timescale = 0;
  let kind: Track['kind'] = 'other';
  let codec: string | null = null;
  if (mdia) {
    const ms = mdia.at + mdia.hdr;
    const me = mdia.at + mdia.size;
    const mdhd = find(b, ms, me, 'mdhd');
    if (mdhd) timescale = dv.getUint32(mdhd.at + mdhd.hdr + (b[mdhd.at + mdhd.hdr] === 1 ? 20 : 12));
    const hdlr = find(b, ms, me, 'hdlr');
    const handler = hdlr ? String.fromCharCode(...b.subarray(hdlr.at + hdlr.hdr + 8, hdlr.at + hdlr.hdr + 12)) : '';
    kind = handler === 'vide' ? 'video' : handler === 'soun' ? 'audio' : 'other';
    const minf = find(b, ms, me, 'minf');
    const stbl = minf && find(b, minf.at + minf.hdr, minf.at + minf.size, 'stbl');
    const stsd = stbl && find(b, stbl.at + stbl.hdr, stbl.at + stbl.size, 'stsd');
    // stsd: version/flags, entry count, then the first sample entry.
    const entry = stsd && children(b, stsd.at + stsd.hdr + 8, stsd.at + stsd.size)[0];
    if (entry) codec = sampleEntryCodec(b, entry);
  }
  return { id, timescale, kind, codec };
}

function sampleEntryCodec(b: Uint8Array, e: Header & { at: number }): string | null {
  const end = e.at + e.size;
  if (e.type === 'avc1' || e.type === 'avc3') {
    const c = find(b, e.at + e.hdr + 78, end, 'avcC');
    if (!c) return null;
    const p = c.at + c.hdr;
    return `${e.type}.${hex2(b[p + 1]!)}${hex2(b[p + 2]!)}${hex2(b[p + 3]!)}`;
  }
  if (e.type === 'hvc1' || e.type === 'hev1') {
    const c = find(b, e.at + e.hdr + 78, end, 'hvcC');
    return c ? hevcCodec(e.type, b.subarray(c.at + c.hdr, c.at + c.size)) : null;
  }
  if (e.type === 'mp4a') {
    const c = find(b, e.at + e.hdr + 28, end, 'esds');
    return `mp4a.40.${c ? (aacObjectType(b.subarray(c.at + c.hdr + 4, c.at + c.size)) ?? 2) : 2}`;
  }
  return null;
}

/** hvc1.<space><profile>.<compat, bit-reversed>.<tier><level>[.<constraint bytes>] (ISO/IEC 14496-15 annex E). */
export function hevcCodec(type: string, c: Uint8Array): string | null {
  if (c.length < 13) return null;
  const space = ['', 'A', 'B', 'C'][c[1]! >> 6]!;
  const tier = c[1]! & 0x20 ? 'H' : 'L';
  const profile = c[1]! & 0x1f;
  let compat = view(c).getUint32(2);
  let rev = 0;
  for (let i = 0; i < 32; i++) {
    rev = (rev << 1) | (compat & 1);
    compat >>>= 1;
  }
  const cons = [...c.subarray(6, 12)];
  while (cons.length && cons.at(-1) === 0) cons.pop();
  return `${type}.${space}${profile}.${(rev >>> 0).toString(16).toUpperCase()}.${tier}${c[12]}${cons.map((x) => `.${x.toString(16).toUpperCase()}`).join('')}`;
}

/** The AAC audio object type from an esds descriptor list, if there is one. */
function aacObjectType(d: Uint8Array): number | null {
  let o = 0;
  const len = () => {
    let n = 0;
    for (let i = 0; i < 4 && o < d.length; i++) {
      const x = d[o++]!;
      n = (n << 7) | (x & 0x7f);
      if (!(x & 0x80)) break;
    }
    return n;
  };
  while (o < d.length) {
    const tag = d[o++]!;
    const n = len();
    if (tag === 0x03) {
      const flags = d[o + 2]!;
      o += 3 + (flags & 0x80 ? 2 : 0) + (flags & 0x20 ? 1 + d[o + 3]! : 0) + (flags & 0x40 ? 2 : 0);
    } else if (tag === 0x04) {
      o += 13;
    } else if (tag === 0x05) {
      return n ? d[o]! >> 3 : null;
    } else {
      o += n;
    }
  }
  return null;
}

const TFXD = [0x6d, 0x1d, 0x9b, 0x05, 0x42, 0xd5, 0x44, 0xe6, 0x80, 0xe2, 0x14, 0x1d, 0xaf, 0xf7, 0x57, 0xb2];

export interface Fragment {
  /** The moof with a tfdt per traf, for Media Source. */
  moof: Uint8Array;
  /** Wall time (epoch ms) the fragment's first track covers, from its start to its end. */
  startMs: number;
  endMs: number;
}

/**
 * Puts one moof on the shared timeline: each traf gets a tfdt (version 1) at its media time plus the stream's offset
 * from `anchorMs`, and the trun data offsets move by the bytes added. `running` keeps per-track media time for streams
 * without tfxd or tfdt. Null when the fragment can't be placed (an unknown track, or data offsets this can't move).
 */
export function placeFragment(moof: Uint8Array, init: Init, streamStartMs: number, anchorMs: number, running: Map<number, number>): Fragment | null {
  const dv = view(moof);
  const trafs: { size: number; parts: Uint8Array[] }[] = [];
  let first: { startMs: number; endMs: number } | null = null;
  for (const c of children(moof, 8)) {
    if (c.type !== 'traf') continue;
    const kids = children(moof, c.at + c.hdr, c.at + c.size);
    const tfhd = kids.find((k) => k.type === 'tfhd');
    if (!tfhd) return null;
    const tfhdFlags = dv.getUint32(tfhd.at + tfhd.hdr) & 0xffffff;
    if (tfhdFlags & 0x1) return null; // base-data-offset: offsets from the file start, which a stream doesn't have
    const trackId = dv.getUint32(tfhd.at + tfhd.hdr + 4);
    const track = init.tracks.find((t) => t.id === trackId);
    if (!track || !track.timescale) return null;
    let p = tfhd.at + tfhd.hdr + 8 + (tfhdFlags & 0x1 ? 8 : 0) + (tfhdFlags & 0x2 ? 4 : 0);
    const defaultDuration = tfhdFlags & 0x8 ? dv.getUint32(p) : 0;
    // media time: tfxd (NX), else an existing tfdt, else where this track's last fragment ended
    let time: number | null = null;
    let length = 0;
    for (const k of kids) {
      const at = k.at + k.hdr;
      if (k.type === 'uuid' && TFXD.every((x, i) => moof[at + i] === x)) {
        const v1 = moof[at + 16] === 1;
        time = v1 ? Number(dv.getBigUint64(at + 20)) : dv.getUint32(at + 20);
        length = v1 ? Number(dv.getBigUint64(at + 28)) : dv.getUint32(at + 24);
      } else if (k.type === 'tfdt' && time === null) {
        time = moof[at] === 1 ? Number(dv.getBigUint64(at + 4)) : dv.getUint32(at + 4);
      }
    }
    // the trun's sample durations give the length when tfxd doesn't
    let sum = 0;
    for (const k of kids.filter((x) => x.type === 'trun')) {
      const at = k.at + k.hdr;
      const flags = dv.getUint32(at) & 0xffffff;
      const n = dv.getUint32(at + 4);
      p = at + 8 + (flags & 0x1 ? 4 : 0) + (flags & 0x4 ? 4 : 0);
      const per = (flags & 0x100 ? 4 : 0) + (flags & 0x200 ? 4 : 0) + (flags & 0x400 ? 4 : 0) + (flags & 0x800 ? 4 : 0);
      for (let i = 0; i < n; i++) sum += flags & 0x100 ? dv.getUint32(p + i * per) : defaultDuration;
    }
    if (!length) length = sum;
    if (time === null) time = running.get(trackId) ?? 0;
    running.set(trackId, time + length);
    const offset = Math.round(((streamStartMs - anchorMs) * track.timescale) / 1000);
    const decode = time + offset;
    if (decode < 0) return null;
    if (!first) first = { startMs: streamStartMs + (time * 1000) / track.timescale, endMs: streamStartMs + ((time + length) * 1000) / track.timescale };
    // rebuilt traf: tfhd, the new tfdt, the rest (an old tfdt dropped)
    const tfdt = new Uint8Array(20);
    const tv = view(tfdt);
    tv.setUint32(0, 20);
    tfdt.set([0x74, 0x66, 0x64, 0x74], 4);
    tv.setUint32(8, 0x01000000);
    tv.setBigUint64(12, BigInt(decode));
    const parts: Uint8Array[] = [];
    for (const k of kids) {
      if (k.type === 'tfdt') continue;
      parts.push(moof.subarray(k.at, k.at + k.size));
      if (k === tfhd) parts.push(tfdt);
    }
    trafs.push({ size: c.size, parts });
  }
  if (!first || !trafs.length) return null;

  // Assemble: the moof's other boxes as they were, each traf rebuilt; then move every trun data offset by the growth.
  const out: Uint8Array[] = [];
  let grown = 0;
  const trafSizes = trafs.map((t) => 8 + t.parts.reduce((n, x) => n + x.length, 0));
  trafs.forEach((t, i) => (grown += trafSizes[i]! - t.size));
  const total = moof.length + grown;
  const head = new Uint8Array(8);
  view(head).setUint32(0, total);
  head.set(moof.subarray(4, 8), 4);
  out.push(head);
  let ti = 0;
  for (const c of children(moof, 8)) {
    if (c.type !== 'traf') {
      out.push(moof.subarray(c.at, c.at + c.size));
      continue;
    }
    const t = trafs[ti]!;
    const th = new Uint8Array(8);
    view(th).setUint32(0, trafSizes[ti]!);
    th.set([0x74, 0x72, 0x61, 0x66], 4);
    out.push(th, ...t.parts.map((x) => x.slice()));
    ti++;
  }
  const result = new Uint8Array(total);
  let o = 0;
  for (const x of out) {
    result.set(x, o);
    o += x.length;
  }
  // data offsets count from the moof's first byte to the sample data in the mdat after it, which moved by `grown`
  const rv = view(result);
  for (const c of children(result, 8)) {
    if (c.type !== 'traf') continue;
    for (const k of children(result, c.at + c.hdr, c.at + c.size)) {
      if (k.type !== 'trun') continue;
      if (rv.getUint32(k.at + k.hdr) & 0x1) rv.setInt32(k.at + k.hdr + 8, rv.getInt32(k.at + k.hdr + 8) + grown);
    }
  }
  return { moof: result, ...first };
}
